import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

// mui components
import { Box, Stack } from "@mui/material";

// mui icons
import CloseIcon from "@mui/icons-material/Close";

// mui icons
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

// custom components
import Body1 from "../../typography/body-01";
import Heading3 from "../../typography/heading-03";
import PrimaryButton from "../../buttons/primary-button";
import TertiaryButton from "../../buttons/tertiary-button";
import RequestLockCard from "../request-lock";
import LockExtensionRequestCard from "../lock-extension-request";
import LockParameterCard from "../lock-parameter";
import LoanParametersSummaryCard from "../loan-parameters-summary";
import ReLockRequestCard from "../relock-request";

// custom modal
import LoaderTextModal from "../../modal/loader-text-modal";
import SuccessModal from "../../modal/success-modal";
import ErrorModal from "../../modal/error-modal";
import ProcessingModal from "../../modal/processing-modal";
import LoaderModal from "../../modal/loader-modal";

// helper functions
import { filterValue } from "../../../utils/filter-value";
import { TPO_SECONDARY } from "../../../utils/common-constants";
import {
	selectedUserWithAccessLevel,
	selectedUserWithLoanChannel,
	selectedUser,
} from "../../../utils/common-helpers";
import { canRequestLockActions } from "../../../utils/lock-action-permissions";

// api services
import { useFieldMutation } from "../../../features/field-values/fieldValueApiSlice";
import { useLockExtensionPolicyMutation } from "../../../features/lock-extension-policy/lockExtensionPolicyApiSlice";
import { useRelockPolicyMutation } from "../../../features/relock-policy/relockPolicyApiSlice";
import { useGetOBLoanDataMutation } from "../../../features/get-ob-loan-data/getOBLoanDataApiSlice";
import { useGetOBChangeRequestsMutation } from "../../../features/get-ob-change-requests/getOBChangeRequestsApiSlice";

// reducer slices
import {
	selectUserDetails,
	selectSelectedOrganization,
	selectOrganizationName,
} from "../../../features/auth/authSlice";
import { selectImpersonatedUser } from "../../../features/admin/impersonated-user/impersonatedUserSlice";

// custom styles
import styles from "./index.module.scss";
import {
	convertCSTtoEST,
	convertUTCtoEst,
} from "../../../utils/timezone-offset";

const LockInfoCard = ({ loanData, handleTabChange }) => {
	const [field] = useFieldMutation();
	const [lockExtensionRequestPolicy, { isLoading: isGettingLockLoanPolicy }] =
		useLockExtensionPolicyMutation();
	const [relockRequestPolicy, { isLoading: isGettingRelockLoanPolicy }] =
		useRelockPolicyMutation();
	const [getOBLoanData, { isLoading: isGettinggetOBLoanDataMutation }] =
		useGetOBLoanDataMutation();
	const [
		getOBChangeRequests,
		{ isLoading: isGettinggetOBChangeRequestsMutation },
	] = useGetOBChangeRequestsMutation();
	const [fieldValues, setFieldValues] = useState([]);
	const [selectedViewIndex, setSelectedViewIndex] = useState(0);
	const userDetails = useSelector(selectUserDetails);
	const impersonatedUser = useSelector(selectImpersonatedUser);
	const selectedOrganization = useSelector(selectSelectedOrganization);
	const organizationName = useSelector(selectOrganizationName);
	const currentOrganization = selectedOrganization || organizationName;
	const [obEntityID, setOBEntityID] = useState("");
	const [obUserID, setOBUserID] = useState("");
	const [obLoanID, setOBLoanID] = useState("");
	const [checkRequestLock, setCheckRequestLock] = useState(true);
	const [
		checkLoanChannelAndUserTitlePermissions,
		setCheckLoanChannelAndUserTitlePermissions,
	] = useState(true);
	const [
		checkLockExtensionAllowedAPIsPermissions,
		setCheckLockExtensionAllowedAPIsPermissions,
	] = useState(true);
	const [
		checkRelockAllowedAPIsPermissions,
		setCheckRelockAllowedAPIsPermissions,
	] = useState(true);
	const [lockRequestedOn, setLockRequestedOn] = useState("NA");
	const [lockStatus, setLockStatus] = useState("NA");
	const [isLockStatusRegistered, setIsLockStatusRegistered] =
		useState("Registered");
	const [pendingStatus, setPendingStatus] = useState("NA");

	const currentUserTitle = selectedUser(
		userDetails,
		impersonatedUser
	)?.user_title;
	const loanChannel = selectedUserWithLoanChannel(
		userDetails,
		impersonatedUser
	)?.loan_channel;

	const [processingText, setProcessingText] = useState("");
	const [processingModalVisible, setProcessingModalVisible] = useState(false);

	const handleOpenProcessingModal = () => {
		setProcessingModalVisible(true);
	};

	const handleCloseProcessingModal = () => {
		setProcessingModalVisible(false);
	};

	const [successText, setSuccessText] = useState("");
	const [successModalVisible, setSuccessModalVisible] = useState(false);

	const handleOpenSuccessModal = () => {
		setSuccessModalVisible(true);
	};

	const handleCloseSuccessModal = () => {
		setSuccessModalVisible(false);
	};

	const [errorText, setErrorText] = useState("");
	const [errorModalVisible, setErrorModalVisible] = useState(false);

	const handleOpenErrorModal = () => {
		setErrorModalVisible(true);
	};

	const handleCloseErrorModal = () => {
		setErrorModalVisible(false);
	};

	const handleButtonsViews = (event, index) => {
		setSelectedViewIndex(index);
	};

	const getFieldValues = async () => {
		console.log("75 loan id:", loanData?.encompassId);
		console.log("75 loan number:", loanData?.loanNumber);

		try {
			const getValues = await field({
				loan_guid: loanData?.encompassId,
				fields:
					"2,URLA.X73,URLA.X75,12,14,4000,4001,4002,4004,4005,4006,3,2400,VASUMM.X23,Log.MS.CurrentMilestone,1401,LCP.X1,CX.LOCKREQUESTTIMESTAMP,2149,2151,3420,2202,2203,2160,2089,762,761,CX.OBTIME,2161,19,1811,1041,356,136,1109,353,976,1540,742,CX.DEBTSERVICECOVERAGERATIO,CX.AUTODEBIT,1550,CX.OB.ENTITY.ID,CX.OBLOANID",
				selectedOrganization: currentOrganization,
				userDetails,
			}).unwrap();

			const data = getValues?.response || [];
			setFieldValues(data);

			const entityField = filterValue(data, "CX.OB.ENTITY.ID");
			if (entityField !== "") {
				const parts = entityField.split(")")[1].trim();
				const [entity, userID] = parts.split("*");

				setOBEntityID(entity);
				setOBUserID(userID);
				setOBLoanID(filterValue(data, "CX.OBLOANID"));
				setCheckRequestLock(filterValue(data, "761") === "" ? false : true);
			}
		} catch (err) {
			console.log("270 error:", err);
		}
	};

	const isEnabledLockExtensionRequestByDate = (expirationDateStr) => {
		console.log("9090:", expirationDateStr);
		if (
			!expirationDateStr ||
			expirationDateStr === "NA" ||
			expirationDateStr === ""
		)
			return true;

		const today = convertUTCtoEst(new Date());
		console.log("9090:", today);
		console.log("909090:", Date.parse(expirationDateStr), Date.parse(today));
		if (Date.parse(expirationDateStr) < Date.parse(today)) return true;

		return false;
	};

	// LC-698: role/channel gate for Lock Extension and Re-Lock requests.
	// Broker Admin is identified by access level, not user title.
	const isEnabledLockByLoanChannelAndUserTitle = async () =>
		canRequestLockActions({
			loanChannel,
			userTitles: currentUserTitle,
			accessLevel: selectedUserWithAccessLevel(userDetails, impersonatedUser)
				?.access_level,
		});

	const isEnabledLockExtensionRequestByPolicy = async () => {
		if (
			isEnabledLockExtensionRequestByDate(
				convertCSTtoEST(filterValue(fieldValues, "762") || "NA")
			) === true
		) {
			console.log("Step: 1", true);
			return true;
		}

		if (checkLoanChannelAndUserTitlePermissions === false) {
			console.log("Step: 2", true);
			return true;
		}
		try {
			const getResponse = await lockExtensionRequestPolicy({
				ob_loan_id: obLoanID,
				ob_business_channel_id: obEntityID,
				ob_originator_id: obUserID,
			}).unwrap();

			console.log("lock extension all fields:", getResponse);
			if (getResponse.status_code === 200) {
				console.log(
					"isEnabledLockExtensionRequestByPolicy 2222:",
					!getResponse?.data?.allowed
				);
				console.log("Step: 3", !getResponse?.data?.allowed);
				return !getResponse?.data?.allowed;
			}
		} catch (err) {
			console.log("270 error:", err);
			return true;
		}
		return true;
	};

	const isEnabledReLockRequestByPolicy = async () => {
		if (checkLoanChannelAndUserTitlePermissions === false) {
			console.log("Step: Relock 2", true);
			return true;
		}
		try {
			const getResponse = await relockRequestPolicy({
				ob_loan_id: obLoanID,
				ob_business_channel_id: obEntityID,
				ob_originator_id: obUserID,
			}).unwrap();

			console.log("Re-Lock request all fields:", getResponse);
			if (getResponse.status_code === 200) {
				console.log(
					"isEnabledReLockRequestByPolicy 2222:",
					!getResponse?.data?.allowed
				);
				return !getResponse?.data?.allowed;
			}
		} catch (err) {
			console.log("270 error:", err);
			return true;
		}
		return true;
	};

	const getOBLoan = async () => {
		try {
			const getResponse = await getOBLoanData({
				ob_loan_id: obLoanID,
				ob_business_channel_id: obEntityID,
				ob_originator_id: obUserID,
			}).unwrap();

			console.log("Get OB all fields:", getResponse);
			if (getResponse.status_code === 200) {
				console.log("Get OB All fields:", getResponse?.data);
				return getResponse?.data;
			}
		} catch (err) {
			console.log("270 error:", err);
			return err;
		}
	};

	const getOBChangeRequest = async () => {
		try {
			const getResponse = await getOBChangeRequests({
				ob_loan_id: obLoanID,
				ob_business_channel_id: obEntityID,
				ob_originator_id: obUserID,
			}).unwrap();

			console.log("Get OB Change Requests:", getResponse);
			if (getResponse.status_code === 200) {
				console.log("Get OB Change Requests:", getResponse?.data);
				return getResponse?.data;
			}
		} catch (err) {
			console.log("270 error:", err);
			return err;
		}
	};

	useEffect(() => {
		if (loanData?.encompassId && userDetails && currentOrganization) {
			getFieldValues();
		}
	}, [loanData?.encompassId, currentOrganization, userDetails]);

	useEffect(() => {
		const checkLoanChannelAndUserTitleResult = async () => {
			const result = await isEnabledLockByLoanChannelAndUserTitle();
			setCheckLoanChannelAndUserTitlePermissions(result);
		};
		checkLoanChannelAndUserTitleResult();
	}, [loanChannel, userDetails]);

	useEffect(() => {
		// run only when all fields are set
		if (!obEntityID || !obUserID || !obLoanID) return;

		const lockExtensionResult = async () => {
			const result = await isEnabledLockExtensionRequestByPolicy();
			console.log("API LOCK EXTENSION RESULT:", result, "TYPE:", typeof result);
			setCheckLockExtensionAllowedAPIsPermissions(result);
		};
		lockExtensionResult();

		const relockResult = async () => {
			const result = await isEnabledReLockRequestByPolicy();
			console.log("API RELOCK RESULT:", result, "TYPE:", typeof result);
			setCheckRelockAllowedAPIsPermissions(result);
		};
		relockResult();

		const getOBLoanDataResult = async () => {
			const result = await getOBLoan();
			console.log("API OB LOAN RESULT:", result, "TYPE:", typeof result);
			console.log(
				"API OB LOAN result?.data?.status:",
				result?.status,
				"TYPE:",
				typeof result
			);
			setLockRequestedOn(result?.lockRequestedOn || "NA");
			setLockStatus(
				result?.status === "Locked" &&
					isEnabledLockExtensionRequestByDate(
						convertCSTtoEST(filterValue(fieldValues, "762"))
					) === true
					? "Lock Expired"
					: result?.status === "LockPending"
						? "Lock Pending"
						: result?.status
			);
			setIsLockStatusRegistered(result?.status !== "Registered");
		};
		getOBLoanDataResult();

		const getOBChangeRequestsResult = async () => {
			const result = await getOBChangeRequest();
			console.log(
				"API OB CHANGE REQUEST RESULT:",
				result,
				"TYPE:",
				typeof result
			);
			const pendingItem = result.find(
				(item) => item.status?.toLowerCase() === "pending"
			);

			if (pendingItem) {
				setPendingStatus(
					`Pending ${pendingItem.type} - ${convertUTCtoEst(pendingItem.submittedDateTime, { includeTime: true, use12Hour: true, appendET: true })}`
				);
			} else {
				setPendingStatus("NA");
			}
		};
		getOBChangeRequestsResult();
	}, [obEntityID, obUserID, obLoanID, selectedViewIndex]);

	function renderView(index) {
		if (index === 1)
			return (
				<RequestLockCard
					loanData={loanData}
					getFieldValuesOnLoanSummary={getFieldValues}
					goToDefaultView={() => setSelectedViewIndex(0)}
				/>
			);
		else if (index === 2)
			return (
				<LockExtensionRequestCard
					loanData={loanData}
					getFieldValuesOnLoanSummary={getFieldValues}
					lockRequestedOn={lockRequestedOn}
					lockStatus={lockStatus}
					goToDefaultView={() => setSelectedViewIndex(0)}
				/>
			);
		else if (index === 3)
			return (
				<ReLockRequestCard
					loanData={loanData}
					getFieldValuesOnLoanSummary={getFieldValues}
					lockRequestedOn={lockRequestedOn}
					lockStatus={lockStatus}
					goToDefaultView={() => setSelectedViewIndex(0)}
				/>
			);
		else if (index === 4)
			return (
				<div
					style={{
						fontSize: "2em",
						fontWeight: 600,
						color: "black",
						padding: "10px",
						borderRadius: "4px",
						textAlign: "center",
						margin: "20px auto",
					}}
				>
					Change Request
				</div>
			);
		else
			return (
				<Stack direction="column" spacing={2}>
					<LoanParametersSummaryCard
						loanData={loanData}
						fieldData={fieldValues}
					/>
					<LockParameterCard
						loanData={loanData}
						fieldData={fieldValues}
						lockRequestedOn={lockRequestedOn}
						lockStatus={lockStatus}
						pendingStatus={pendingStatus}
					/>
				</Stack>
			);
	}

	return (
		<Stack direction="column" className={styles.stackContainer}>
			<Stack
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				className={styles.topContainer}
			>
				<Heading3
					text="Lock Information and Activities"
					extraClass={styles.tabTitle}
					fontType="semibold"
				/>

				<Stack
					direction="row"
					alignItems="center"
					justifyContent="flex-end"
					className={styles.topBtnContainer}
				>
					<TertiaryButton
						startIcon={<CloseIcon />}
						onClick={(e) => handleTabChange(e, false)}
					/>
				</Stack>
			</Stack>
			<Stack
				direction="column"
				alignItems="center"
				justifyContent="space-between"
				className={styles.topContainer}
			>
				<Stack
					direction="row"
					alignItems="center"
					justifyContent="space-between"
					className={styles.topBtnContainer}
				>
					{[
						{
							text: "Request Lock",
							id: 1,
							disabled: checkRequestLock || isLockStatusRegistered,
						},
						{
							text: "Lock Extension Request",
							id: 2,
							disabled: checkLockExtensionAllowedAPIsPermissions,
						},
						{
							text: "Re-Lock Request",
							id: 3,
							disabled: checkRelockAllowedAPIsPermissions,
						},
						// { text: "Change Request", id: 4, disabled:  true },
					].map((btn) => (
						<PrimaryButton
							key={btn.id}
							text={btn.text}
							extraClass={`${styles.buttonViewStyle} ${
								selectedViewIndex === btn.id ? styles.activeButtonStyle : ""
							}`}
							onClick={(e) => handleButtonsViews(e, btn.id)}
							disabled={btn.disabled}
						/>
					))}
				</Stack>
			</Stack>
			{/* custom modals */}
			<LoaderTextModal
				open={
					isGettingLockLoanPolicy ||
					isGettingRelockLoanPolicy ||
					isGettinggetOBLoanDataMutation ||
					isGettinggetOBChangeRequestsMutation
				}
				loadingTextLine1={""}
				loadingTextLine2={"This will take a few minutes"}
			/>
			<ProcessingModal open={processingModalVisible} text={processingText} />
			<SuccessModal
				open={successModalVisible}
				handleClose={handleCloseSuccessModal}
				text={successText}
			/>
			<ErrorModal
				open={errorModalVisible}
				text={errorText}
				handleClose={handleCloseErrorModal}
			/>
			<Stack direction="row" className={styles.bottomContainer}>
				{renderView(selectedViewIndex)}
			</Stack>
		</Stack>
	);
};

export default LockInfoCard;
